import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import axios from "axios";
import styled, { css } from "styled-components";
import dayjs, { Dayjs } from "dayjs";
import _theme from "../styles/theme";
import Card from "./Card";
import FlexBox, { FlexGrow } from "./FlexBox";
import { RightAlign } from "./UtilsComponents";
import Status from "../types/enum/Status";
import Button from "./Button";
import Icon, { ClickableIcon } from "./Icon";
import StudentProgramDropdown from "./StudentProgramDropdown";
import AddProgramModal from "./AddProgramModal";
import EditStudentProgramModal from "./EditForms/EditStudentProgramModal";
import GraduateStudentProgramModal from "./GraduateStudentProgramModal";
import WithdrawStudentProgramModal from "./WithdrawStudentProgramModal";
import LeaveProgramModal from "./LeaveProgramModal";
import SnackBar from "./SnackBar";
import utc from "dayjs/plugin/utc";
import EditGraduateProgramModal from "./EditForms/EditGraduateProgramModal";
import SuspendProgramModal from "./SuspendProgramModal";
import TerminateProgramModal from "./TerminateProgramModal";
import AlternativeAwardModal from "./AlternativeAwardModal";
import EnrollModal from "./EnrollModal";
import LeaveModal from "./LeaveModal";
import EditAlternativeAwardModal from "./EditForms/EditAlternativeAwardModal";
import EditSuspendModal from "./EditForms/EditSuspendModal";

dayjs.extend(utc);
const ProgramStyled = styled(FlexBox)`
  display: flex;
  justify-content: space-between;
`;

type ViewMode = "view" | "edit";
type UserType = "admin" | "student";
/* eslint-disable camelcase */
interface User {
  user_id: string;
  student_id: string;
  enrolled_date: string;
  cmkl_id: string;
  expected_graduation_date: string;
  activity_type: {
    id: number;
    name: string;
  };
  programs: Array<any>;
  honor: {
    id: string;
    honor_name: string;
  };
  expected_graduation_term: {
    id: string;
    name: string;
  };
}

type StudentInfoForm = {
  enrolled_date: string | undefined;
  cmkl_id: string | undefined;
  expected_graduation_date: string | undefined;
  activity_type_id: number | undefined;
  expected_graduation_term_id: string | undefined;
  program_id: string | undefined;
  honor_id: string | undefined;
  expected_graduation_term: string | undefined;
};
type StudentInfoProps = {
  defViewMode: ViewMode;
  userType: UserType;
  userInfo: any;
  information_confirm: boolean;
  reloadParent: any;
};
type ModalDataContent = {
  student_id: string;
  program_id: string;
  source_program_id?: string;
  source_program_name?: string;
  with_transfer?: boolean;
  activity_date: string;
  enrolled_date: string;
  activity_type_id: number;
  activity_type: string;
  expected_graduation_date?: string;
  expected_graduation_term_id?: string;
  honor_id: string;
  thesis_title?: string;
  thesis_completed?: boolean;
  thesis_completed_date?: string;
  on_leave?: boolean;
  on_leave_date?: string;
  on_probation?: boolean;
  alternative_degree_award_date?: string;
};
const StudentInformation = ({
  defViewMode,
  userType,
  userInfo,
  information_confirm,
  reloadParent,
}: StudentInfoProps) => {
  const validationSchema = Yup.object({
    enroll_date: Yup.string(),
    expected_graduation_date: Yup.string(),
    activity_type_id: Yup.number(),
    expected_graduation_term_id: Yup.string(),
    program_id: Yup.string(),
    cmkl_id: Yup.string(),
  });
  const {
    formState: { errors, isDirty },
  } = useForm<StudentInfoForm>({
    resolver: yupResolver(validationSchema),
  });

  // Loading text on "Enrolled Courses"
  const [loading, setLoading] = useState<boolean>(true);
  const [viewMode, setViewMode] = useState<ViewMode>(defViewMode);
  // Enrolled Course Information for CourseInfoModal
  const [verified, setVerified] = useState<boolean>(false);

  const [honors, setHonors] = useState([
    {
      label: "",
      value: "",
    },
  ]);

  const [expandedProgram, setExpandedProgram] = useState<any>(null);
  const [modalData, setModalData] = useState<{
    type:
    | ""
    | "new"
    | "edit"
    | "edit-graduate"
    | "edit-alternative-award"
    | "edit-suspend"
    | "withdraw"
    | "graduate"
    | "leave"
    | "un_leave"
    | "suspend"
    | "terminate"
    | "alternative-award"
    | "enroll";
    data: ModalDataContent;
  }>({
    type: "",
    data: {
      student_id: userInfo?.student_id,
      program_id: "",
      enrolled_date: new Date().toISOString(),
      activity_date: new Date().toISOString(),
      activity_type_id: 4,
      activity_type: "ENROLLED",
      expected_graduation_date: new Date().toISOString(),
      expected_graduation_term_id: "",
      honor_id: "",
    },
  });

  function checkEditing() {
    if (userType === "admin") {
      return false;
    }
    if (userType === "student" && !verified) {
      return false;
    }
    if (userType === "student" && verified) {
      return true;
    }
    return false;
  }

  return (
    <>
      {modalData.type === "suspend" && (
        <SuspendProgramModal
          reloadParent={(message: string) => {
            reloadParent(message);
            setModalData({ ...modalData, type: "" });
          }}
          isOpen={modalData.type === "suspend"}
          setOpen={() => { }}
          modalData={modalData}
          setModalData={setModalData}
        />
      )}
      {modalData.type === "terminate" && (
        <TerminateProgramModal
          reloadParent={(message: string) => {
            reloadParent(message);
            setModalData({ ...modalData, type: "" });
          }}
          isOpen={modalData.type === "terminate"}
          setOpen={() => { }}
          modalData={modalData}
          setModalData={setModalData}
        />
      )}
      {modalData.type === "alternative-award" && (
        <AlternativeAwardModal
          reloadParent={(message: string) => {
            reloadParent(message);
            setModalData({ ...modalData, type: "" });
          }}
          isOpen={modalData.type === "alternative-award"}
          setOpen={() => { }}
          modalData={modalData}
          setModalData={setModalData}
        />
      )}
      {modalData.type === "enroll" && (
        <EnrollModal
          reloadParent={(message: string) => {
            reloadParent(message);
            setModalData({ ...modalData, type: "" });
          }}
          isOpen={modalData.type === "enroll"}
          setOpen={() => { }}
          modalData={modalData}
          setModalData={setModalData}
        />
      )}

      {modalData.type === "new" && (
        <AddProgramModal
          userInfo={userInfo}
          reloadParent={(message: string) => {
            reloadParent(message);
            setModalData({ ...modalData, type: "" });
          }}
          isOpen={modalData.type === "new"}
          setOpen={() => { }}
          modalData={modalData}
          setModalData={setModalData}
        />
      )}
      {modalData.type === "edit" && (
        <EditStudentProgramModal
          reloadParent={(message: string) => {
            reloadParent(message);
            setModalData({ ...modalData, type: "" });
          }}
          isOpen={modalData.type === "edit"}
          setOpen={() => { }}
          modalData={modalData}
          setModalData={setModalData}
        />
      )}
      {modalData.type === "edit-graduate" && (
        <EditGraduateProgramModal
          reloadParent={(message: string) => {
            reloadParent(message);
            setModalData({ ...modalData, type: "" });
          }}
          isOpen={modalData.type === "edit-graduate"}
          setOpen={() => { }}
          modalData={modalData}
          setModalData={setModalData}
        />
      )}
      {modalData.type === "edit-alternative-award" && (
        <EditAlternativeAwardModal
          reloadParent={(message: string) => {
            reloadParent(message);
            setModalData({ ...modalData, type: "" });
          }}
          isOpen={modalData.type === "edit-alternative-award"}
          setOpen={() => { }}
          modalData={modalData}
          setModalData={setModalData}
        />
      )}
      {modalData.type === "edit-suspend" && (
        <EditSuspendModal
          reloadParent={(message: string) => {
            reloadParent(message);
            setModalData({ ...modalData, type: "" });
          }}
          isOpen={modalData.type === "edit-suspend"}
          setOpen={() => { }}
          modalData={modalData}
          setModalData={setModalData}
        />
      )}
      {modalData.type === "graduate" && (
        <GraduateStudentProgramModal
          reloadParent={(message: string) => {
            reloadParent(message);
            setModalData({ ...modalData, type: "" });
          }}
          isOpen={modalData.type === "graduate"}
          setOpen={() => { }}
          modalData={modalData}
          setModalData={setModalData}
        />
      )}
      {modalData.type === "withdraw" && (
        <WithdrawStudentProgramModal
          reloadParent={(message: string) => {
            reloadParent(message);
            setModalData({ ...modalData, type: "" });
          }}
          isOpen={modalData.type === "withdraw"}
          setOpen={() => { }}
          modalData={modalData}
          setModalData={setModalData}
        />
      )}
      {/* {modalData.type === "leave" && (
        <LeaveProgramModal
          reloadParent={(message: string) => {
            reloadParent(message);
            setModalData({ ...modalData, type: "" });
          }}
          isOpen={modalData.type === "leave"}
          setOpen={() => { }}
          modalData={modalData}
          setModalData={setModalData}
        />
      )} */}

      {modalData.type === "leave" && (
        <LeaveModal
          reloadParent={(message: string) => {
            reloadParent(message);
            setModalData({ ...modalData, type: "" });
          }}
          isOpen={modalData.type === "leave"}
          setOpen={() => { }}
          modalData={modalData}
          setModalData={setModalData}
        />
      )}
      {modalData.type === "un_leave" && (
        <LeaveProgramModal
          reloadParent={(message: string) => {
            reloadParent(message);
            setModalData({ ...modalData, type: "" });
          }}
          isOpen={modalData.type === "un_leave"}
          setOpen={() => { }}
          modalData={modalData}
          setModalData={setModalData}
        />
      )}
      <Card style={{ margin: "24px 0px" }}>
        <FlexBox
          alignItems="center"
          style={{
            marginBottom: _theme.spacing.x2,
          }}
        >
          <FlexGrow>
            <h3>Programs</h3>
          </FlexGrow>
          <FlexGrow ml-auto style={{ marginLeft: "auto" }}>
            <RightAlign>
              {viewMode === "view" && userType !== "student" && (
                <>
                  <Button
                    onClick={() => {
                      setModalData({
                        type: "new",
                        data: {
                          ...modalData.data,
                          student_id: userInfo?.student_id,
                          activity_date: new Date().toISOString(),
                          activity_type_id: 4,
                          activity_type: "ENROLLED",
                        },
                      });
                    }}
                    disabled={checkEditing()}
                    isOutline
                    status={Status.primary}
                    style={{ padding: "4px 12px" }}
                  >
                    <Icon name="plus" mr={0.5} />
                    Add Program
                  </Button>
                </>
              )}
            </RightAlign>
          </FlexGrow>
        </FlexBox>
        {userInfo?.programs?.map((prog: any) => (
          <div
            style={{
              border: "1px solid #e0e0e0",
              borderRadius: "4px",
              padding: "10px",
              marginBottom: "8px",
            }}
          >
            <ProgramStyled>
              <FlexBox>
                <ClickableIcon
                  name={
                    expandedProgram?.program_id === prog?.program_id
                      ? "chevron-bottom"
                      : "chevron-right"
                  }
                  mr={0.5}
                  style={{ color: _theme.color.primary }}
                  onClick={() =>
                    expandedProgram?.program_id === prog?.program_id
                      ? setExpandedProgram(null)
                      : setExpandedProgram(prog)
                  }
                />

                <FlexBox
                  style={{ flexDirection: "column", alignItems: "start" }}
                >
                  {/* <span style={{lineHeight: "1", fontWeight:'bold'}}> B.E. AiCE</span>
              <span style={{lineHeight: "1", color:'green', fontWeight:'bold', fontSize:'12px'}}>Enrolled</span> */}
                  <span style={{ fontWeight: "bold" }}>
                    {prog.program_name} ({prog.short_name})
                  </span>
                  <span
                    style={{
                      lineHeight: "1",
                      color:
                        prog.status?.toLowerCase() === "enrolled"
                          ? _theme.color.success
                          : prog.status?.toLowerCase() === "withdrawn" || prog.status?.toLowerCase() === "terminated"
                            ? _theme.color.danger
                            : prog.status?.toLowerCase() === "suspended" ? _theme.color.warningDark
                              : prog.status?.toLowerCase() === "graduated"
                                ? _theme.color.blue
                                : prog.status?.toLowerCase() === "on leave"
                                  ? "gray" : prog.status?.toLowerCase() === "alternative degree award" ? _theme.color.blueDark
                                    : "black",
                      fontWeight: "bold",
                      fontSize: "12px",
                    }}
                  >
                    {prog.status}
                  </span>
                </FlexBox>
              </FlexBox>
              {prog.status !== "WITHDRAWN" && prog.status !== "TERMINATED" &&
                // prog.status !== "GRADUATED" &&
                userType === "admin" && (
                  <FlexBox>
                    <StudentProgramDropdown  // Drop down for program edit, graduate, withdraw, and leave actions, etc....
                      program={prog}
                      openEditModal={() => {
                        setModalData({
                          type: "edit",
                          data: {
                            student_id: userInfo?.student_id,
                            activity_date: prog?.enrolled_date,
                            enrolled_date: prog?.enrolled_date,
                            activity_type_id:
                              prog?.status === "ON LEAVE"
                                ? 7
                                : prog?.status === "RETURN FROM LEAVE"
                                  ? 8
                                  : 4,
                            activity_type:
                              prog?.status === "ON LEAVE"
                                ? "ON LEAVE"
                                : prog?.status === "RETURN FROM LEAVE"
                                  ? "RETURN FROM LEAVE"
                                  : "ENROLLED",
                            program_id: prog?.program_id,
                            expected_graduation_term_id:
                              prog?.expected_graduation_term?.id,
                            expected_graduation_date:
                              prog?.expected_graduation_date,
                            honor_id: prog?.honor_id,
                            thesis_title: prog?.thesis_title,
                            thesis_completed: prog?.thesis_completed,
                            thesis_completed_date: prog?.thesis_completed
                              ? prog?.thesis_completed_date
                              : new Date().toISOString(),
                            on_probation: prog?.on_probation || false,
                          },
                        });
                      }}
                      openEditGraduateModal={() => {
                        setModalData({
                          type: "edit-graduate",
                          data: {
                            student_id: userInfo?.student_id,
                            activity_date: prog?.activity_date,
                            enrolled_date: prog?.enrolled_date,
                            activity_type_id: 6,
                            activity_type: "GRADUATED",
                            program_id: prog?.program_id,
                            expected_graduation_term_id:
                              prog?.expected_graduation_term?.id,
                            expected_graduation_date:
                              prog?.expected_graduation_date,
                            honor_id: prog?.honor_id,
                            thesis_title: prog?.thesis_title,
                            thesis_completed: prog?.thesis_completed,
                            thesis_completed_date: prog?.thesis_completed
                              ? prog?.thesis_completed_date
                              : new Date().toISOString(),
                            on_probation: prog?.on_probation || false,
                          },
                        });
                      }}
                      openEditSuspendModal={() => {
                        setModalData({
                          type: "edit-suspend",
                          data: {
                            student_id: userInfo?.student_id,
                            activity_date: prog?.activity_date,
                            enrolled_date: prog?.enrolled_date,
                            activity_type_id: 8,
                            activity_type: "SUSPENDED",
                            program_id: prog?.program_id,
                            expected_graduation_term_id:
                              prog?.expected_graduation_term?.id,
                            expected_graduation_date:
                              prog?.expected_graduation_date,
                            honor_id: prog?.honor_id,
                            thesis_title: prog?.thesis_title,
                            thesis_completed: prog?.thesis_completed,
                            thesis_completed_date: prog?.thesis_completed
                              ? prog?.thesis_completed_date
                              : new Date().toISOString(),
                            on_probation: prog?.on_probation || false,
                          },
                        });
                      }}
                      openEditAlternativeAwardModal={() => {
                        setModalData({
                          type: "edit-alternative-award",
                          data: {
                            student_id: userInfo?.student_id,
                            activity_date: prog?.activity_date,
                            enrolled_date: prog?.enrolled_date,
                            activity_type_id: 9,
                            activity_type: "ALTERNATIVE DEGREE AWARD",
                            program_id: prog?.program_id,
                            alternative_degree_award_date: prog?.alternative_degree_award_date,
                            expected_graduation_term_id:
                              prog?.expected_graduation_term?.id,
                            expected_graduation_date:
                              prog?.expected_graduation_date,
                            honor_id: prog?.honor_id,
                            thesis_title: prog?.thesis_title,
                            thesis_completed: prog?.thesis_completed,
                            thesis_completed_date: prog?.thesis_completed
                              ? prog?.thesis_completed_date
                              : new Date().toISOString(),
                            on_probation: prog?.on_probation || false,
                          },
                        });
                      }}
                      openGraduateModal={() => {
                        setModalData({
                          type: "graduate",
                          data: {
                            student_id: userInfo?.student_id,
                            activity_date: new Date().toISOString(),
                            activity_type_id: 6,
                            activity_type: "GRADUATED",
                            enrolled_date: prog?.enrolled_date,
                            program_id: prog?.program_id,
                            expected_graduation_term_id:
                              prog?.expected_graduation_term?.id,
                            expected_graduation_date:
                              prog?.expected_graduation_date,
                            honor_id: prog?.honor_id,
                          },
                        });
                      }}
                      openWithdrawModal={() => {
                        setModalData({
                          type: "withdraw",
                          data: {
                            student_id: userInfo?.student_id,
                            activity_date: new Date().toISOString(),
                            enrolled_date: prog?.enrolled_date,
                            activity_type_id: 3,
                            activity_type: "WITHDRAWN",
                            program_id: prog?.program_id,
                            expected_graduation_term_id:
                              prog?.expected_graduation_term?.id,
                            expected_graduation_date:
                              prog?.expected_graduation_date,
                            honor_id: prog?.honor_id,
                          },
                        });
                      }}
                      // openLeaveModal={() => {
                      //   setModalData({
                      //     type:
                      //       prog.status === "ON LEAVE" ? "un_leave" : "leave",
                      //     data: {
                      //       student_id: userInfo?.student_id,
                      //       activity_date: new Date().toISOString(),
                      //       enrolled_date: prog?.enrolled_date,
                      //       activity_type_id:
                      //         prog.status === "ON LEAVE" ? 4 : 7,
                      //       activity_type:
                      //         prog.status === "ON LEAVE"
                      //           ? "ENROLLED"
                      //           : "ON LEAVE",
                      //       program_id: prog?.program_id,
                      //       expected_graduation_term_id:
                      //         prog?.expected_graduation_term?.id,
                      //       expected_graduation_date:
                      //         prog?.expected_graduation_date,
                      //       honor_id: prog?.honor_id,
                      //       on_leave: prog.status === "ON LEAVE" ? true : false,
                      //       on_leave_date: prog.on_leave_date,
                      //     },
                      //   });
                      // }}
                      openLeaveModal={() => {
                        setModalData({
                          type: "leave",
                          data: {
                            student_id: userInfo?.student_id,
                            activity_date: new Date().toISOString(),
                            enrolled_date: prog?.enrolled_date,
                            activity_type_id: 7,
                            activity_type: "ON LEAVE",
                            program_id: prog?.program_id,
                            expected_graduation_term_id:
                              prog?.expected_graduation_term?.id,
                            expected_graduation_date:
                              prog?.expected_graduation_date,
                            honor_id: prog?.honor_id,
                            on_leave: prog.status === "ON LEAVE" ? true : false,
                            on_leave_date: prog.on_leave_date,
                          },
                        });
                      }}
                      openSuspendModal={() => {
                        setModalData({
                          type: "suspend",
                          data: {
                            student_id: userInfo?.student_id,
                            activity_date: new Date().toISOString(),
                            enrolled_date: prog?.enrolled_date,
                            activity_type_id: 8,
                            activity_type: "SUSPENDED",
                            program_id: prog?.program_id,
                            expected_graduation_term_id:
                              prog?.expected_graduation_term?.id,
                            // expected_graduation_date:
                            //   prog?.expected_graduation_date,
                            honor_id: prog?.honor_id,
                          },
                        });
                      }}
                      openTerminateModal={() => {
                        setModalData({
                          type: "terminate",
                          data: {
                            student_id: userInfo?.student_id,
                            activity_date: new Date().toISOString(),
                            enrolled_date: prog?.enrolled_date,
                            activity_type_id: 5,
                            activity_type: "TERMINATED",
                            program_id: prog?.program_id,
                            expected_graduation_term_id:
                              prog?.expected_graduation_term?.id,
                            // expected_graduation_date:
                            //   prog?.expected_graduation_date,
                            honor_id: prog?.honor_id,
                          },
                        });
                      }}

                      openAlternativeAward={() => {
                        setModalData({
                          type: "alternative-award",
                          data: {
                            student_id: userInfo?.student_id,
                            activity_date: new Date().toISOString(),
                            enrolled_date: prog?.enrolled_date,
                            activity_type_id: 9,
                            activity_type: "ALTERNATIVE DEGREE AWARD",
                            program_id: prog?.program_id,
                            expected_graduation_term_id:
                              prog?.expected_graduation_term?.id,
                            // expected_graduation_date:
                            //   prog?.expected_graduation_date,
                            honor_id: prog?.honor_id,
                          },
                        });
                      }}
                      openEnrollModal={() => {
                        setModalData({
                          type: "enroll",
                          data: {
                            student_id: userInfo?.student_id,
                            activity_date: new Date().toISOString(),
                            enrolled_date: prog?.enrolled_date,
                            activity_type_id: 5,
                            activity_type: "ENROLLED",
                            program_id: prog?.program_id,
                            expected_graduation_term_id:
                              prog?.expected_graduation_term?.id,
                            // expected_graduation_date:
                            //   prog?.expected_graduation_date,
                            honor_id: prog?.honor_id,
                            on_probation: prog?.on_probation || false,
                          },
                        });
                      }}
                    />
                  </FlexBox>
                )}
            </ProgramStyled>

            {expandedProgram &&
              expandedProgram?.program_id === prog?.program_id && (
                <div style={{ padding: "10px 40px", color: "#333" }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "300px auto",
                      rowGap: "8px",
                    }}
                  >
                    <strong>Enrolled Date</strong>
                    <strong>
                      :{" "}
                      {expandedProgram?.enrolled_date
                        ? dayjs(expandedProgram.enrolled_date)
                          .utc()
                          ?.format("DD-MM-YYYY")
                        : "N/A"}
                    </strong>

                    {expandedProgram.status === "SUSPENDED" && (
                      <>
                        <strong>Suspended Date</strong>
                        <strong>
                          :{" "}
                          {expandedProgram?.suspended_date
                            ? dayjs(expandedProgram.suspended_date)
                              .utc()
                              ?.format("DD-MM-YYYY")
                            : "N/A"}
                        </strong>
                      </>
                    )}

                    {expandedProgram.status === "TERMINATED" && (
                      <>
                        <strong>Terminated Date</strong>
                        <strong>
                          :{" "}
                          {expandedProgram?.terminated_date
                            ? dayjs(expandedProgram.terminated_date)
                              .utc()
                              ?.format("DD-MM-YYYY")
                            : "N/A"}
                        </strong>
                      </>
                    )}

                    {expandedProgram.status === "WITHDRAWN" && (
                      <>
                        <strong>Withdrawn Date</strong>
                        <strong>
                          :{" "}
                          {expandedProgram?.activity_date
                            ? dayjs(expandedProgram.withdrawn_date)
                              .utc()
                              ?.format("DD-MM-YYYY")
                            : "N/A"}
                        </strong>
                      </>
                    )}

                    {expandedProgram.status === "ON LEAVE" && (
                      <>
                        <strong>Left Date</strong>
                        <strong>
                          :{" "}
                          {expandedProgram?.on_leave_date
                            ? dayjs(expandedProgram.on_leave_date)
                              .utc()
                              ?.format("DD-MM-YYYY")
                            : "N/A"}
                        </strong>
                      </>
                    )}
                    {expandedProgram.status === "GRADUATED" && (
                      <>
                        <strong>Graduated Date</strong>
                        <strong>
                          :{" "}
                          {expandedProgram?.graduated_date
                            ? dayjs(expandedProgram.graduated_date)
                              .utc()
                              ?.format("DD-MM-YYYY")
                            : "N/A"}
                        </strong>
                      </>
                    )}
                    {expandedProgram.status === "ALTERNATIVE DEGREE AWARD" && (
                      <>
                        <strong>Alternative Degree Awarded Date</strong>
                        <strong>
                          :{" "}
                          {expandedProgram?.alternative_degree_award_date
                            ? dayjs(expandedProgram.alternative_degree_award_date)
                              .utc()
                              ?.format("DD-MM-YYYY")
                            : "N/A"}
                        </strong>
                      </>
                    )}
                    {!expandedProgram?.expected_graduation_date.includes(
                      "0001-01-01"
                    ) && (
                        <>
                          <strong>Expected Graduation Date</strong>
                          <strong>
                            :{" "}
                            {expandedProgram?.expected_graduation_date
                              ? dayjs(expandedProgram.expected_graduation_date)
                                ?.utc()
                                ?.format("DD-MM-YYYY")
                              : "N/A"}
                          </strong>
                        </>
                      )}

                    <strong>Expected Graduation Term</strong>
                    <strong>
                      :{" "}
                      {expandedProgram?.expected_graduation_term?.name || "N/A"}
                    </strong>

                    <strong>Honor</strong>
                    <strong>: {expandedProgram?.honor_name || "N/A"}</strong>

                    {expandedProgram?.status === "Withdrawn" && (
                      <>
                        <strong>Withdrawn Date</strong>
                        <strong>
                          :{" "}
                          {expandedProgram?.withdrawn_date
                            ? dayjs(expandedProgram.withdrawn_date)
                              .utc()
                              ?.format("DD-MM-YYYY")
                            : "N/A"}
                        </strong>
                      </>
                    )}

                    {/* {expandedProgram?.status === "GRADUATED" && (
                      <>
                        <strong>Graduation Date</strong>
                        <strong>: {expandedProgram?.graduated_date ? dayjs(expandedProgram.graduated_date)
                          ?.utc()
                          ?.format("DD-MM-YYYY") : "N/A"}</strong>
                      </>
                    )} */}
                    {expandedProgram?.status !== "GRADUATED" && expandedProgram?.status !== "ALTERNATIVE DEGREE AWARD" && expandedProgram?.status !== "GRADUATED" && (
                      <>
                        <strong>On Probation</strong>
                        <strong>: {expandedProgram.on_probation ? "Yes" : "No"}</strong>
                      </>
                    )}
                  </div>
                </div>
              )}
          </div>
        ))}
      </Card>
    </>
  );
};

export default StudentInformation;
